import { Pagination, PickerProfile, ZoneIdentifier } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { PickerProfilePanel } from './PickerProfilePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ZoneChip from 'src/ZoneManagement/Components/ZoneChip';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

// ZoneChip chip height (48px) + top/bottom margin (8 + 8) = 64px.
// Shared across all scrollable columns so scroll indicators align.
const ROW_MIN_HEIGHT = 64;

export const PickerProfileManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();

  const [, startLoading] = useRequest();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<PickerProfile>();

  const dismiss = useCallback(() => {
    setDialogOpen(false);
    setPanelOpen(false);
    setSelectedProfile(undefined);
  }, []);

  const fetchProfiles = useCallback((pagination: Pagination) => api.getPickerProfiles(pagination), [api]);

  const deleteProfile = useCallback(async () => {
    if (selectedProfile === undefined) {
      return;
    }

    const { zone, name } = selectedProfile;

    startLoading(
      () => api.deletePickerProfile(zone as ZoneIdentifier, name),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api, selectedProfile]);

  return (
    <ContentControl
      panel={<PickerProfilePanel profile={selectedProfile} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth="sm"
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title="Delete Picker Profile"
          text={selectedProfile?.name ?? ''}
          onClose={dismiss}
          onConfirm={deleteProfile}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Picker Profile'}
          onClick={() => {
            dismiss();
            setSelectedProfile(undefined);
            setPanelOpen(true);
            scrollTop();
          }}
          requiredPermission={Permissions.PICKER_PROFILES_WRITE}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: PickerProfile) => {
              dismiss();
              setSelectedProfile(entry);
              setPanelOpen(true);
              scrollTop();
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: PickerProfile) => {
              dismiss();
              setSelectedProfile(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={
          [
            {
              header: 'Name',
              body: (entry: PickerProfile, searchText?: string) => (
                <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                  {searchText ? dataTableHighlight(entry.name ?? '', [searchText]) : entry.name}
                </ScrollableStack>
              ),
              filterField: 'name',
              sortField: 'PP.name',
              proportionalContent: '40%',
            },
            {
              header: 'Coordinate',
              body: (entry: PickerProfile, searchText?: string) => (
                <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                  {searchText ? dataTableHighlight(entry.coordinate ?? '', [searchText]) : entry.coordinate}
                </ScrollableStack>
              ),
              filterField: 'coordinate',
              sortField: 'PP.materializedPath',
              proportionalContent: '60%',
            },
            {
              header: 'Zone',
              body: (entry: PickerProfile, searchText?: string) => (
                <ZoneChip zone={entry.zone} highlightText={searchText} />
              ),
              fitContent: true,
            },
            {
              header: 'Strategy',
              body: (entry: PickerProfile) => entry.startupStrategy.name,
              fitContent: true,
            },
          ] as ColumnDef[]
        }
        fetchItems={fetchProfiles}
      />
    </ContentControl>
  );
};
